/**
 * 服务端玩法格 ↔ 前端 Tile 模型的映射（对应 GET /admin/api/config/tiles）。
 *
 * 与 server-config.ts 同一哲学：两边刻意不同形——服务端按表存
 * （bps 整数、bands 按 kind 分组的行），前端按编辑心智组织
 * （×g 小数、reel/tier/schemes 三个具名数组）。映射集中在这，谁演进都只改这里。
 *
 * bps ↔ 倍数换算只发生在这一层：服务端整数下发没有 0.3/3000 的二义，
 * 前端拿到的永远是 ×g 倍数小数。
 */
import type { ReelRow, SchemeRow, SizeRow, Tile, TierRow } from "./tiles";
import { cleanTileParams } from "../../../src/game/tile-params.ts";
import { ACT_ITEM_TILE } from "../../../src/game/act-item-tile.ts";

/* ── 服务端形状（与 src/admin/tiles.ts 对齐） ── */
export interface ServerTileBand {
  bandId: string;
  sortOrder: number;
  gMinBps: number | null;
  gMaxBps: number | null;
  hitCount: number | null;
  missCount: number | null;
  weight: number | null;
  payload: Record<string, unknown>;
}
export interface ServerTile {
  tileTypeId: string;
  cat: string;
  schemaId: string;
  enabled: boolean;
  carryMinBps: number | null;
  carryMaxBps: number | null;
  triggerPity: number | null;
  triggerCooldown: number | null;
  animMs: number;
  skippable: boolean;
  sortOrder: number;
  skinId: string;
  extraDropChannelId: string;
  boxTotal: number | null;
  minClientVersion: string | null;
  /** 所属副玩法组（A–E）；老接口不带 = [] */
  groups?: string[];
  nameI18n: Record<string, string>;
  params: Record<string, unknown>;
  bands: Record<string, ServerTileBand[]>;
}
export interface ServerTilesPayload {
  configVersion: number;
  revision: number;
  status: string;
  tiles: ServerTile[];
}

const bpsToG = (v: number | null): number | null => (v == null ? null : v / 10000);
const gToBps = (v: number | null | undefined): number | null => (v == null ? null : Math.round(v * 10000));
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const numOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** 服务端 → 前端。空 tiles 版本照样能开页（返回 []），前端再决定是否播种默认七格 */
export function toTile(st: ServerTile): Tile {
  const sorted = (kind: string) => [...(st.bands[kind] ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const reel: ReelRow[] | undefined = st.bands.reel
    ? sorted("reel").map((b) => ({
        gMin: bpsToG(b.gMinBps), gMax: bpsToG(b.gMaxBps),
        comboId: str(b.payload.comboId), art: str(b.payload.art), label: str(b.payload.label),
      }))
    : undefined;
  const tier: TierRow[] | undefined = st.bands.tier
    ? sorted("tier").map((b) => ({
        gMin: bpsToG(b.gMinBps), gMax: bpsToG(b.gMaxBps),
        animLevel: str(b.payload.animLevel), desc: str(b.payload.desc),
      }))
    : undefined;
  const size: SizeRow[] | undefined = st.bands.size
    ? sorted("size").map((b) => ({ gMin: bpsToG(b.gMinBps), gMax: bpsToG(b.gMaxBps), size: str(b.payload.size) }))
    : undefined;
  const schemes: SchemeRow[] | undefined = st.bands.scheme
    ? sorted("scheme").map((b) => ({ id: b.bandId, hit: b.hitCount, miss: b.missCount, weight: b.weight }))
    : undefined;

  const p = st.params;
  return {
    key: st.tileTypeId,
    id: st.tileTypeId,
    name: str(st.nameI18n.en),
    ja: str(st.nameI18n.ja),
    cat: st.cat === "EVENT" ? "EVENT" : "ECONOMY",
    // label 是菜单 i18n key；服务端来的 tile 没有内置 key，前端菜单按 tile_type_id 拼
    label: `nav.subgame-${st.tileTypeId}` as Tile["label"],
    skin: st.skinId,
    enabled: st.enabled,
    minClient: st.minClientVersion ?? null,
    groups: st.groups ?? [],
    carryMin: bpsToG(st.carryMinBps) ?? undefined,
    carryMax: bpsToG(st.carryMaxBps) ?? undefined,
    pity: null,
    cooldown: null,
    anim: st.animMs,
    skip: st.skippable,
    sort: st.sortOrder,
    drop: st.extraDropChannelId,
    boxTotal: st.boxTotal ?? undefined,
    jitter: numOrNull(p.jitter) ?? undefined,
    roundTo: numOrNull(p.roundTo) ?? undefined,
    npc: typeof p.npc === "string" ? p.npc : undefined,
    text: typeof p.text === "string" ? p.text : undefined,
    ...(st.tileTypeId === ACT_ITEM_TILE ? { actItem: {
      value: Number(p.value ?? 0), fallbackKind: p.fallbackKind === "dice" ? "dice" : "coin", fallbackValue: Number(p.fallbackValue ?? 0),
    } } : {}),
    reel,
    tier,
    size,
    schemes,
    note: `tile.note.${st.tileTypeId}` as Tile["note"],
  };
}

export const toTiles = (tiles: ServerTile[]): Tile[] => tiles.map(toTile);

/**
 * 前端 → 写接口的 TileInput（PUT /config/versions/:v/tiles/:id 的 body.tile）。
 *
 * **必须带上读取时的原始 ServerTile 作底座（base）**：putTile 是整对象替换，
 * 而 toTile 是有损展示映射（只取 en/ja、已知 params、已知 payload 字段）。
 * 不合并底座的话，「改了个动画时长」就会把韩语名、未知演出参数、
 * 自定义 payload 字段、稳定 band_id、手工指定的 schema_id 全部静默抹掉。
 * base 传 null 表示新建（没有可保的东西）。
 */
export function toServerTile(t: Tile, base: ServerTile | null): {
  tileTypeId: string; cat: string; schemaId: string; enabled: boolean;
  carryMinBps: number | null; carryMaxBps: number | null;
  triggerPity: number | null; triggerCooldown: number | null;
  animMs: number; skippable: boolean; sortOrder: number; skinId: string;
  extraDropChannelId: string; boxTotal: number | null;
  minClientVersion: string | null;
  groups: string[];
  nameI18n: Record<string, string>; params: Record<string, unknown>;
  bands: Array<{ bandKind: string; bandId: string; sortOrder: number; gMinBps: number | null; gMaxBps: number | null; hitCount: number | null; missCount: number | null; weight: number | null; payload: Record<string, unknown> }>;
} {
  // schema_id：底座优先——手工指定过的协定不许被结构反推悄悄改写；
  // 只有新建（无底座）才按结构猜。发布器对未注册的 id 会拒发。
  const inferred = t.reel ? "slot_reel" : t.tier ? "jackpot_tier" : t.size ? "fountain_size" : t.schemes ? "vault_scheme" : t.cat === "EVENT" ? "event_plain" : "plain";
  const schemaId = base?.schemaId ?? inferred;

  // band：按 kind 内的**行位**对齐底座——同位行保留原 band_id 与未知 payload 字段，
  // UI 能编辑的字段覆盖上去；多出的行造新 id（避开已占用的）。
  const baseKind = (kind: string) => [...(base?.bands[kind] ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  // 已占用号 = 底座里的 ∪ 本次已分配的。只查底座的话，一次新增两行
  // 会各自算出同一个空位号，写库直接撞主键
  const usedIds = new Map<string, Set<string>>();
  const claim = (kind: string, id: string): string => {
    let set = usedIds.get(kind);
    if (!set) { set = new Set((base?.bands[kind] ?? []).map((b) => b.bandId)); usedIds.set(kind, set); }
    set.add(id);
    return id;
  };
  const freshId = (kind: string, prefix: string, i: number): string => {
    let set = usedIds.get(kind);
    if (!set) { set = new Set((base?.bands[kind] ?? []).map((b) => b.bandId)); usedIds.set(kind, set); }
    let n = i + 1;
    while (set.has(`${prefix}${n}`)) n++;
    const id = `${prefix}${n}`;
    set.add(id);
    return id;
  };
  const bands: ReturnType<typeof toServerTile>["bands"] = [];
  const baseReel = baseKind("reel");
  (t.reel ?? []).forEach((r, i) => bands.push({
    bandKind: "reel", bandId: baseReel[i] ? claim("reel", baseReel[i].bandId) : freshId("reel", "r", i), sortOrder: i,
    gMinBps: gToBps(r.gMin), gMaxBps: gToBps(r.gMax), hitCount: null, missCount: null, weight: null,
    payload: { ...(baseReel[i]?.payload ?? {}), comboId: r.comboId, art: r.art, label: r.label },
  }));
  const baseTier = baseKind("tier");
  (t.tier ?? []).forEach((r, i) => bands.push({
    bandKind: "tier", bandId: baseTier[i] ? claim("tier", baseTier[i].bandId) : freshId("tier", "t", i), sortOrder: i,
    gMinBps: gToBps(r.gMin), gMaxBps: gToBps(r.gMax), hitCount: null, missCount: null, weight: null,
    payload: { ...(baseTier[i]?.payload ?? {}), animLevel: r.animLevel, desc: r.desc },
  }));
  const baseSize = baseKind("size");
  (t.size ?? []).forEach((r, i) => bands.push({
    bandKind: "size", bandId: baseSize[i] ? claim("size", baseSize[i].bandId) : freshId("size", "s", i), sortOrder: i,
    gMinBps: gToBps(r.gMin), gMaxBps: gToBps(r.gMax), hitCount: null, missCount: null, weight: null,
    payload: { ...(baseSize[i]?.payload ?? {}), size: r.size },
  }));
  const baseScheme = baseKind("scheme");
  (t.schemes ?? []).forEach((r, i) => bands.push({
    bandKind: "scheme", bandId: r.id, sortOrder: i,
    gMinBps: null, gMaxBps: null, hitCount: r.hit, missCount: r.miss, weight: r.weight,
    payload: { ...(baseScheme.find((b) => b.bandId === r.id)?.payload ?? {}) },
  }));

  // params / nameI18n：底座打底，UI 已知字段覆盖。UI 清空（null/undefined）已知字段时
  // 从底座里删掉——不删的话「清空」永远清不掉
  const params: Record<string, unknown> = cleanTileParams(t.id, base?.params ?? {});
  const act = t.id === ACT_ITEM_TILE ? t.actItem : undefined;
  for (const [k, v] of Object.entries({ jitter: t.jitter, roundTo: t.roundTo, npc: t.npc, text: t.text,
    ...(t.id === ACT_ITEM_TILE ? { value: act?.value, fallbackKind: act?.fallbackKind, fallbackValue: act?.fallbackValue } : {}) })) {
    if (v == null) delete params[k];
    else params[k] = v;
  }
  const nameI18n: Record<string, string> = { ...(base?.nameI18n ?? {}) };
  if (t.name) nameI18n.en = t.name; else delete nameI18n.en;
  if (t.ja) nameI18n.ja = t.ja; else delete nameI18n.ja;

  return {
    tileTypeId: t.id, cat: t.cat, schemaId, enabled: t.enabled,
    carryMinBps: gToBps(t.carryMin ?? null), carryMaxBps: gToBps(t.carryMax ?? null),
    triggerPity: null, triggerCooldown: null,
    animMs: t.anim ?? 0, skippable: t.skip, sortOrder: t.sort ?? 0,
    skinId: t.skin, extraDropChannelId: t.drop,
    boxTotal: t.boxTotal ?? null,
    minClientVersion: t.minClient?.trim() || null,
    groups: t.groups ?? [],
    nameI18n, params, bands,
  };
}
